import React, { useEffect, useRef, useState } from 'react';

const steps = [
  { id: '01', title: 'Discovery Phase', description: 'We start with thorough discovery sessions to understand your project’s specific needs, goals, and technical feasibility, laying a timeline for success.' },
  { id: '02', title: 'Analysis and Planning', description: 'Based on the pre-decided scope, we develop a detailed project plan, outlining key milestones, deliverables, and stakeholders’ roles.' },
  { id: '03', title: 'UI/UX Design', description: 'From wireframing to prototyping, our designers work closely with you to create custom UI/UX designs resulting in engaging user experience.' },
  { id: '04', title: 'Architecture Design', description: 'Our technical teams develop robust, scalable architecture designs that ensure system reliability, flexibility, and maintainability.' },
  { id: '05', title: 'Software Development', description: 'Our experts follow best practices to write clean, efficient, scalable code using modern languages and frameworks.' },
  { id: '06', title: 'QA & Testing', description: 'Thorough testing including unit, integration, system and user acceptance testing to deliver a high-quality product.' },
  { id: '07', title: 'Deployment & Launch', description: 'We deploy your solution to your chosen environment (cloud, on-premise, hybrid) with minimal downtime.' },
  { id: '08', title: 'Maintenance & Updates', description: 'Ongoing support with security patches, bug fixes, performance improvements and new features.' },
];

const AgileProcess = () => {
  const sectionRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const [isPinned, setIsPinned] = useState(false);

  // Pin logic — صرف desktop پر چلے گی
  useEffect(() => {
    const updatePinState = () => {
      // موبائل/ٹیبلٹ پر pinning مت چلائیں
      if (window.innerWidth < 1024) {
        setIsPinned(false);
        return;
      }

      if (!leftRef.current || !sectionRef.current) return;

      const leftRect = leftRef.current.getBoundingClientRect();
      const sectionRect = sectionRef.current.getBoundingClientRect();

      const isNearTop = leftRect.top <= 100;
      const sectionEnded = sectionRect.bottom <= window.innerHeight + 50;

      setIsPinned(isNearTop && !sectionEnded);
    };

    updatePinState();
    window.addEventListener('scroll', updatePinState, { passive: true });
    window.addEventListener('resize', updatePinState, { passive: true });

    return () => {
      window.removeEventListener('scroll', updatePinState);
      window.removeEventListener('resize', updatePinState);
    };
  }, []);

  // Scroll hijacking — صرف جب isPinned true ہو (یعنی desktop پر)
  useEffect(() => {
    // موبائل پر کچھ نہیں کرنا
    if (window.innerWidth < 1024) {
      document.body.style.overflow = '';
      return;
    }

    const right = rightRef.current;
    if (!isPinned || !right || !sectionRef.current) {
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = 'hidden';

    const handleWheel = (e) => {
      e.preventDefault();

      const scrollTop = right.scrollTop;
      const maxScroll = right.scrollHeight - right.clientHeight;

      const atTop = scrollTop <= 3;
      const atBottom = scrollTop >= maxScroll - 3;

      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;

      if ((scrollingUp && atTop) || (scrollingDown && atBottom)) {
        document.body.style.overflow = '';
        setTimeout(() => {
          if (rightRef.current) {
            rightRef.current.scrollTop = Math.max(0, Math.min(maxScroll, rightRef.current.scrollTop));
          }
        }, 40);
        return;
      }

      right.scrollTop += e.deltaY * 1.6;
    };

    const section = sectionRef.current;
    section.addEventListener('wheel', handleWheel, { passive: false });

    // Touch — موبائل پر تو ویسے بھی نہیں چلے گی کیونکہ isPinned false ہوگا
    let touchStartY = 0;

    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      if (!right) return;

      const deltaY = touchStartY - e.touches[0].clientY;

      const scrollTop = right.scrollTop;
      const maxScroll = right.scrollHeight - right.clientHeight;

      const atTop = scrollTop <= 3;
      const atBottom = scrollTop >= maxScroll - 3;

      if ((deltaY < 0 && atTop) || (deltaY > 0 && atBottom)) {
        return;
      }

      e.preventDefault();
      right.scrollTop += deltaY * 2.2;
      touchStartY = e.touches[0].clientY;
    };

    section.addEventListener('touchstart', handleTouchStart, { passive: true });
    section.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      document.body.style.overflow = '';
      section.removeEventListener('wheel', handleWheel);
      section.removeEventListener('touchstart', handleTouchStart);
      section.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isPinned]);

  return (
    <section
      ref={sectionRef}
      className="relative bg-white py-10 md:py-10 lg:py-10 mt-10"
    >
      <div className="mx-auto w-[92%] max-w-7xl flex flex-col lg:grid lg:grid-cols-12 lg:gap-x-12 xl:gap-x-16">
        {/* LEFT */}
        <div
          ref={leftRef}
          className="lg:col-span-6 lg:sticky lg:top-20 h-fit pb-5 lg:pb-0 m-auto"
        >
          <h1 className="text-[26px] md:text-[30px] font-bold mb-6 leading-tight">
            Our Process for the Best <br />
            <span className="text-[#DA2128]">Website Development</span>
          </h1>
          <p className="text-[16px] md:text-[18px] text-black font-bold max-w-lg">
            A Transparent Process, From Discovery to Launch
          </p>
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-6 mt-5 lg:mt-0">
          <div
            ref={rightRef}
            className="lg:h-[75vh] lg:overflow-y-auto scroll-smooth lg:pr-4"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            <style jsx global>{`
              .scroll-smooth::-webkit-scrollbar {
                display: none;
              }
            `}</style>

            <div className="space-y-6 lg:space-y-8 pb-5 lg:pb-40">
              {steps.map((step) => (
                <div
                  key={step.id}
                  className="bg-[gray]-50 p-6 md:p-8 lg:p-10 rounded-2xl lg:rounded-3xl border border-gray-200/70 shadow-sm"
                >
                  <div className="text-5xl md:text-6xl lg:text-7xl font-black text-[#DA2128] mb-4">
                    {step.id}
                  </div>
                  <h3 className="text-[26px] md:text-[30px] font-bold mb-4 text-gray-900 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-[16px] md:text-[18px] text-gray-700 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgileProcess;